import { Mesh, Program, Renderer, Triangle } from 'ogl';
import type { OGLRenderingContext } from 'ogl';

import { PRISM_MIN_DPR } from './prismConstants';
import { calculatePrismPxScale } from './prismResize';
import { PRISM_FRAGMENT_SHADER, PRISM_VERTEX_SHADER } from './prismShaders';
import type { PrismProgram, PrismRuntimeConfig, PrismScene, PrismUniforms } from './prismTypes';

interface CreatePrismSceneInput {
  container: HTMLDivElement;
  runtimeConfig: PrismRuntimeConfig;
  transparent: boolean;
  maxDpr: number;
  onError?: (error: unknown) => void;
}

export function createPrismScene({
  container,
  runtimeConfig,
  transparent,
  maxDpr,
  onError,
}: CreatePrismSceneInput): PrismScene | null {
  const dpr = getPrismDpr(maxDpr);
  const renderer = createPrismRenderer({ dpr, transparent, onError });
  if (!renderer) return null;

  const gl = getPrismGl({ renderer, onError });
  if (!gl) return null;

  preparePrismGl({ gl, container });
  const iResolutionBuffer = new Float32Array(2);
  const offsetPxBuffer = new Float32Array(2);
  const programScene = createPrismProgramScene({
    gl,
    container,
    runtimeConfig,
    iResolutionBuffer,
    offsetPxBuffer,
    onError,
  });
  if (!programScene) return null;

  return {
    renderer,
    gl,
    program: programScene.program,
    mesh: programScene.mesh,
    iResolutionBuffer,
    offsetPxBuffer,
    dpr,
  };
}

function getPrismDpr(maxDpr: number): number {
  return Math.max(PRISM_MIN_DPR, Math.min(maxDpr, window.devicePixelRatio || PRISM_MIN_DPR));
}

interface CreatePrismRendererInput {
  dpr: number;
  transparent: boolean;
  onError?: (error: unknown) => void;
}

function createPrismRenderer({ dpr, transparent, onError }: CreatePrismRendererInput): Renderer | null {
  try {
    return new Renderer({
      dpr,
      alpha: transparent,
      // Premultiplied so Safari and Chrome composite the transparent canvas
      // over the page identically - non-premultiplied output renders far
      // brighter in Safari (it skips the color x alpha step on present).
      premultipliedAlpha: true,
      antialias: false,
    });
  } catch (error) {
    onError?.(error);
    return null;
  }
}

interface GetPrismGlInput {
  renderer: Renderer;
  onError?: (error: unknown) => void;
}

function getPrismGl({ renderer, onError }: GetPrismGlInput): OGLRenderingContext | null {
  const gl = renderer.gl;
  if (!gl) {
    onError?.(new Error('Prism WebGL context is unavailable'));
    return null;
  }
  return gl;
}

interface PreparePrismGlInput {
  gl: OGLRenderingContext;
  container: HTMLDivElement;
}

function preparePrismGl({ gl, container }: PreparePrismGlInput): void {
  gl.disable(gl.DEPTH_TEST);
  gl.disable(gl.CULL_FACE);
  gl.disable(gl.BLEND);

  Object.assign(gl.canvas.style, {
    position: 'absolute',
    inset: '0',
    width: '100%',
    height: '100%',
    display: 'block',
  } as Partial<CSSStyleDeclaration>);
  container.appendChild(gl.canvas);
}

interface CreatePrismProgramSceneInput {
  gl: OGLRenderingContext;
  container: HTMLDivElement;
  runtimeConfig: PrismRuntimeConfig;
  iResolutionBuffer: Float32Array;
  offsetPxBuffer: Float32Array;
  onError?: (error: unknown) => void;
}

interface PrismProgramScene {
  program: PrismProgram;
  mesh: Mesh<Triangle, PrismProgram>;
}

function createPrismProgramScene({
  gl,
  container,
  runtimeConfig,
  iResolutionBuffer,
  offsetPxBuffer,
  onError,
}: CreatePrismProgramSceneInput): PrismProgramScene | null {
  let program: PrismProgram;
  let mesh: Mesh<Triangle, PrismProgram>;

  try {
    const geometry = new Triangle(gl);
    program = new Program(gl, {
      vertex: PRISM_VERTEX_SHADER,
      fragment: PRISM_FRAGMENT_SHADER,
      uniforms: createPrismUniforms({ gl, runtimeConfig, iResolutionBuffer, offsetPxBuffer }),
    }) as PrismProgram;
    mesh = new Mesh(gl, { geometry, program });
  } catch (error) {
    if (gl.canvas.parentElement === container) container.removeChild(gl.canvas);
    onError?.(error);
    return null;
  }

  return {
    program,
    mesh,
  };
}

interface CreatePrismUniformsInput {
  gl: OGLRenderingContext;
  runtimeConfig: PrismRuntimeConfig;
  iResolutionBuffer: Float32Array;
  offsetPxBuffer: Float32Array;
}

function createPrismUniforms({
  gl,
  runtimeConfig,
  iResolutionBuffer,
  offsetPxBuffer,
}: CreatePrismUniformsInput): PrismUniforms {
  return {
    iResolution: { value: iResolutionBuffer },
    iTime: { value: 0 },
    uHeight: { value: runtimeConfig.height },
    uBaseHalf: { value: runtimeConfig.baseHalf },
    uUseBaseWobble: { value: 1 },
    uRot: { value: new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]) },
    uGlow: { value: runtimeConfig.glow },
    uOffsetPx: { value: offsetPxBuffer },
    uNoise: { value: runtimeConfig.noise },
    uSaturation: { value: runtimeConfig.saturation },
    uScale: { value: runtimeConfig.scale },
    uHueShift: { value: runtimeConfig.hueShift },
    uColorFreq: { value: runtimeConfig.colorFrequency },
    uBloom: { value: runtimeConfig.bloom },
    uCenterShift: { value: runtimeConfig.height * 0.25 },
    uInvBaseHalf: { value: 1 / runtimeConfig.baseHalf },
    uInvHeight: { value: 1 / runtimeConfig.height },
    uMinAxis: { value: Math.min(runtimeConfig.baseHalf, runtimeConfig.height) },
    uPxScale: {
      value: calculatePrismPxScale({
        drawingBufferHeight: gl.drawingBufferHeight,
        scale: runtimeConfig.scale,
      }),
    },
    uTimeScale: { value: runtimeConfig.timeScale },
  };
}
